<template>
  <v-container fluid>
    <v-row v-if="isLoading">
      <v-col>
        <loading-spinner></loading-spinner>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12" md="6">
        <paginated-table
          :data="archives"
          :columns="tableColumns"
          :title="$t('title.member_archive')"
          @updatePagination="updatePagination"
        ></paginated-table>
      </v-col>
      <v-col cols="12" md="6">
        <v-card>
          <card-header :title="$t('title.write_form')"></card-header>
          <v-card-text>
            <v-form ref="form" v-model="valid">
              <v-row>
                <v-col>
                  <v-text-field
                    v-model="archive.title"
                    :label="$t('label.title')"
                    type="text"
                    :rules="[rules.required]"
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    v-model="archive.down_file_name"
                    :label="$t('label.file_name')"
                    type="text"
                    :rules="[rules.required]"
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row v-if="!archive.idx">
                <v-col>
                  <upload-file
                    :initialFilePath="archive.file_path"
                    @update-saved-file-path="updateSavedFilePath"
                    @cleared="removeFilePath"
                  ></upload-file>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-card-actions class="mt-3">
            <v-spacer></v-spacer>
            <custom-button
              @click="handleSubmit"
              :label="$t('button.save')"
              :disabled="!valid"
              color="primary"
              size="lg"
              gradient
              rounded
            ></custom-button>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PaginatedTable from "@/components/Tables/PaginatedTable.vue";
import UploadFile from "@/components/Forms/UploadFile.vue";
import CardHeader from "@/components/cards/CardHeader.vue";
import LoadingSpinner from "@/components/common/Spinners/LoadingSpinner.vue";
import CustomButton from "@/components/common/buttons/CustomButton.vue";

import { getMemberArchiveColumnsColumns } from "@/views/AdminView/columns";
import { deletePopup, success, savePopup } from "@/views/AdminView/popups";

import { archiveApi, fileApi } from "@/api/api";

export default {
  components: {
    CardHeader,
    PaginatedTable,
    UploadFile,
    LoadingSpinner,
    CustomButton,
  },
  data() {
    return {
      // boolean
      isLoading: false,

      // fields & columns
      tableColumns: getMemberArchiveColumnsColumns({
        edit: this.handleEdit,
        download: this.handleDownload,
        delete: this.handleDelete,
      }),

      category: "MEMBER",
      archive: {
        title: null,
        down_file_name: null,
      },
      archives: {
        page_info: {
          page: 1,
          size: 5,
          total_elements: 0,
          total_pages: 1,
        },
        elements: [],
      },
      valid: false,
      rules: {
        required: (v) => !!v || "This field is required",
      },
    };
  },
  methods: {
    /**
     * *******************************************************************************************
     *  버튼 액션
     * *******************************************************************************************
     */
    // 수정 버튼 액션
    async handleEdit(archive) {
      await this.fetchArchive(archive);
    },

    // Save button action
    async handleSubmit() {
      if (this.archive.file_path == null || this.archive.file_path == "") {
        await this.notifyVue("top", "center", "파일을 업로드해주세요.");
        return;
      }

      const isConfirmed = await savePopup();

      if (!isConfirmed) {
        return;
      }

      const isCreate = this.archive.idx == null;

      const saveAction = isCreate
        ? this.createArchive(this.archive)
        : this.updateArchive(this.archive);

      const isSuccess = await saveAction;

      if (isSuccess) {
        await this.fetchArchivesByCategory();
        await this.initArchive();
        await success();
      }
    },
    async handleDownload(archive) {
      await this.downloadFile(archive);
    },
    async handleDelete(archive) {
      const isConfirmed = await deletePopup(archive);

      if (!isConfirmed) {
        return;
      }

      const isDeleted = await this.deleteArchive(archive);

      if (isDeleted) {
        await success();
        this.fetchArchivesByCategory();
      }
    },

    /**
     * *******************************************************************************************
     *  API 요청
     * *******************************************************************************************
     */
    async fetchArchivesByCategory() {
      try {
        this.isLoading = true;
        const params = {
          category: this.category,
          page: this.archives.page_info.page,
          size: this.archives.page_info.size,
        };
        const response = await archiveApi.showAllByCateogory(params);
        if (response && response.status === 200) {
          this.archives = response.data.response;
          this.isLoaded = true;
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchArchive(archive) {
      try {
        this.isLoading = true;
        const response = await archiveApi.showArchive(archive);
        if (response && response.status === 200) {
          this.archive = response.data.response;
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    async createArchive() {
      try {
        this.isLoading = true;
        const response = await archiveApi.createArchive(this.archive);
        if (response && response.status === 201) {
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    async deleteArchive(archive) {
      try {
        this.isLoading = true;
        const response = await archiveApi.deleteArchive(archive);
        if (response && response.status === 204) {
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    async updateArchive(archive) {
      try {
        this.isLoading = true;
        const response = await archiveApi.updateArchive(archive);
        if (response && response.status === 200) {
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    async downloadFile(archive) {
      try {
        this.isLoading = true;
        const params = {
          file_name: archive.file_path.split("/").pop(),
        };

        const response = await fileApi.fileDownload(params);

        if (response && response.status === 200) {
          const mimeType = response.headers["content-type"];
          const blob = new Blob([response.data], { type: mimeType });
          const fileURL = URL.createObjectURL(blob);

          // 자동 열기
          if (mimeType === "application/pdf" || mimeType.startsWith("image/")) {
            const link = document.createElement("a");
            link.href = fileURL;
            link.setAttribute("download", archive.down_file_name); // 다운로드 파일 이름 설정
            link.setAttribute("target", "_blank"); // 새 창에서 열기
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
          } else {
            // 다운로드로 처리
            const link = document.createElement("a");
            link.href = fileURL;
            link.setAttribute("download", archive.down_file_name); // 다운로드 파일 이름 설정
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
          }
        }
      } catch (error) {
        alert("파일 다운로드에 실패했습니다. 잠시후 다시 시도해주세요.");
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * *******************************************************************************************
     *  기타 메서드
     * *******************************************************************************************
     */
    /**
     * Pagination 타입 명세
     * @typedef {Object} Conditions
     * @property {string} page - 페이지
     * @property {string} size - 사이즈
     * @property {string} total_elements - 전체 데이터 수
     * @property {string} total_pages - 전체 페이지 수
     */

    /**
     * 페이지네이션 업데이트 메서드
     *
     * @description: 페이징 정보가 변경되면 하위 컴포넌트에서 정보를 받아 업데이트 및 API 요청
     * @params {Pagination} pagination
     */
    updatePagination(pagination) {
      this.archives.page_info.size = pagination.size;
      this.archives.page_info.page = pagination.page;

      this.fetchArchivesByCategory();
    },
    updateSavedFilePath(newPath) {
      this.archive.file_path = newPath;
    },
    initArchive() {
      this.archive = {
        idx: null,
        title: null,
        image_name: null,
        down_file_name: null,
        file_name: null,
        category: "M",
        file_path: "",
      };
    },
    removeFilePath() {
      this.archive.file_path = "";
    },
    notifyVue(verticalAlign, horizontalAlign, alertMessage, alert_type) {
      this.$notify({
        timeout: 5000,
        message: alertMessage,
        icon: "priority_high",
        type: alert_type ? alert_type : "danger",
        horizontalAlign: horizontalAlign,
        verticalAlign: verticalAlign,
      });
    },
  },
  mounted() {
    this.fetchArchivesByCategory();
    this.initArchive();
  },
};
</script>

<style lang="css"></style>
